/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 버튼 클래스를 정의한다.
 *
 * @file /src/components/button/Button.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 14.
 */
import * as parent from '@base/Component';
import { Language, LanguageText } from '@utils/Language';
import { Menu } from '@components/menu/Menu';
import { Properties as MenuItem } from '@components/menu/elements/Items';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Button.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 버튼 토글 상태가 변경되었을 때
     */
    toggle?: (me: T, pressed: boolean) => void | Promise<void>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 버튼아이콘
     */
    icon?: string;

    /**
     * 버튼텍스트
     */
    text?: LanguageText;

    /**
     * 버튼 스타일시트 클래스
     */
    buttonClass?: string;

    /**
     * 토글여부
     */
    toggle?: boolean;

    /**
     * 토글이 활성화된 상태에서 토글활성화여부
     */
    pressed?: boolean;

    /**
     * 버튼값
     */
    value?: any;

    /**
     * 버튼 클릭 핸들러
     */
    handler?: (button: Button, value: any) => Promise<void> | void;

    /**
     * 메뉴버튼
     */
    menu?: Menu | MenuItem[];

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Button extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'button';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'button';

    /**
     * 버튼아이콘
     */
    icon: string | null;

    /**
     * 버튼텍스트
     */
    text: LanguageText | null;

    /**
     * 버튼 스타일시트 클래스
     */
    buttonClass: string | null;

    /**
     * 토글여부
     */
    toggle: boolean;

    /**
     * 토글이 활성화된 상태에서 토글활성화여부
     */
    pressed: boolean;

    /**
     * 버튼값
     */
    value: any;

    /**
     * 버튼 클릭 핸들러
     */
    handler: Function | null;

    /**
     * 메뉴버튼
     */
    menu: Menu;

    /**
     * 버튼 DOM
     */
    $button: Dom = null;

    /**
     * 버튼아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 버튼텍스트 DOM
     */
    $text: Dom = null;

    /**
     * 메뉴버튼 DOM
     */
    $dropdown: Dom = null;

    /**
     * 버튼 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Button> = null) {
        super(properties);

        this.icon = this.properties.icon ?? null;
        this.text = this.properties.text ?? null;
        this.buttonClass = this.properties.buttonClass ?? null;
        this.toggle = this.properties.toggle === true;
        this.pressed = this.properties.pressed === true;

        if (this.pressed == true) {
            this.toggle = true;
        }

        if (this.toggle == true) {
            this.buttonClass = null;
            this.handler = null;
        }

        this.value = this.properties.value ?? null;
        this.handler = this.properties.handler ?? null;
        this.padding = null;

        if ((this.properties.menu ?? null) !== null && this.toggle !== true) {
            this.menu = this.createMenu(this.properties.menu);
        } else {
            this.menu = null;
        }
    }

    /**
     * 메뉴설정이 존재할 경우 메뉴를 생성한다.
     *
     * @param properties - 메뉴설정
     * @returns 메뉴
     */
    protected createMenu(properties: Menu | MenuItem[]): Menu {
        let menu: Menu;
        if (properties instanceof Menu) {
            menu = properties;
        } else {
            menu = new Menu({
                menus: properties,
            });
        }
        menu.setParent(this);

        if (this.handler === null) {
            menu.setTarget(this.$getButton());

            menu.addEvent('beforeShow', () => {
                this.$getButton().addClass('expand');
            });
            menu.addEvent('hide', () => {
                this.$getButton().removeClass('expand');
                this.$getButton().focus();
            });
        } else {
            menu.setTarget(this.$getDropdown());

            menu.addEvent('beforeShow', () => {
                this.$getDropdown().addClass('expand');
            });
            menu.addEvent('hide', () => {
                this.$getDropdown().removeClass('expand');
                this.$getDropdown().focus();
            });
        }

        return menu;
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button', { type: 'button' });

            if (this.buttonClass !== null) {
                this.$button.addClass(...this.buttonClass.split(' '));
            }

            this.$button.append(this.$getIcon());
            this.$button.append(this.$getText());

            if (this.handler !== null || this.toggle == true) {
                this.$button.on('click', () => {
                    this.onClick();
                });
            } else if (this.menu !== null) {
                this.$button.append(Html.create('aside'));
                this.$button.on('keydown', async (_, e) => {
                    if (e.key == 'Enter' || e.key == ' ') {
                        if (this.menu.isShow() == true) {
                            this.menu.hide();
                        } else {
                            this.menu.show();
                        }
                    }

                    if (e.key == 'ArrowDown') {
                        if (this.menu.isShow() == false) {
                            await this.menu.show();
                        }
                        this.menu.focus(true);
                        e.stopPropagation();
                    }
                });

                this.$button.on('pointerdown', async () => {
                    if (this.menu.isShow() == true) {
                        this.menu.hide();
                    } else {
                        this.menu.show();
                    }
                });
            }
        }

        return this.$button;
    }

    /**
     * 버튼아이콘 DOM 을 가져온다.
     *
     * @returns 버튼아이콘 DOM
     */
    $getIcon(): Dom {
        this.$icon ??= Html.icon(this.icon);
        return this.$icon;
    }

    /**
     * 버튼라벨 DOM 을 가져온다.
     *
     * @returns 버튼라벨 DOM
     */
    $getText(): Dom {
        this.$text ??= Html.create('span');
        return this.$text;
    }

    /**
     * 메뉴버튼 DOM 을 가져온다.
     *
     * @returns 메뉴버튼 DOM
     */
    $getDropdown(): Dom {
        if (this.$dropdown === null && this.menu !== null) {
            this.$dropdown = Html.create('button', { type: 'button', role: 'dropdown' }, '<aside></aside>');

            if (this.buttonClass !== null) {
                this.$dropdown.addClass(...this.buttonClass.split(' '));
            }

            this.$dropdown.on('keydown', async (_, e) => {
                if (e.key == 'Enter' || e.key == ' ') {
                    if (this.menu.isShow() == true) {
                        this.menu.hide();
                    } else {
                        this.menu.show();
                    }
                }

                if (e.key == 'ArrowDown') {
                    if (this.menu.isShow() == false) {
                        await this.menu.show();
                    }
                    this.menu.focus(true);
                    e.stopPropagation();
                }
            });

            this.$dropdown.on('pointerdown', () => {
                if (this.menu.isShow() == true) {
                    this.menu.hide();
                } else {
                    this.menu.show();
                }
            });
        }

        return this.$dropdown;
    }

    /**
     * 아이콘을 변경한다.
     *
     * @param icon - 변경할 아이콘 (NULL 인 경우 아이콘을 제거한다.)
     */
    setIcon(icon: string | null = null): void {
        this.icon = icon;
        Html.icon(this.icon, this.$getIcon());
    }

    /**
     * 제목을 변경한다.
     *
     * @param text - 변경할 제목
     */
    async setText(text: LanguageText): Promise<void> {
        this.text = text;

        const $text = this.$getText();
        $text.html(await Language.resolveText(this.text));
    }

    /**
     * 버튼값을 가져온다.
     *
     * @returns 값
     */
    getValue(): string | number {
        return this.value;
    }

    /**
     * 버튼값을 지정한다.
     *
     * @param value - 값
     */
    setValue(value: string | number): void {
        if (this.value !== value) {
            this.value = value;
            this.fireEvent('change', [this, value]);
        }
    }

    /**
     * 토글 상태를 변경한다.
     *
     * @param pressed - 토글여부
     */
    setPressed(pressed: boolean): void {
        this.pressed = pressed;
        this.$getButton().toggleClass('pressed', this.pressed == true);

        this.fireEvent('toggle', [this, this.pressed]);
    }

    /**
     * 컴포넌트에 포커스를 지정한다.
     */
    focus(): void {
        this.$getButton().focus();
    }

    /**
     * 버튼이 눌린 상태인지 가져온다.
     *
     * @returns 눌림여부
     */
    isPressed(): boolean {
        return this.pressed;
    }

    /**
     * 버튼의 로딩상태여부를 설정한다.
     *
     * @param loading - 로딩상태여부
     * @returns 컴포넌트
     */
    setLoading(loading: boolean): this {
        if (loading == true) {
            this.$getButton().addClass('loading');
        } else {
            this.$getButton().removeClass('loading');
        }

        this.setDisabled(loading);
        return this;
    }

    /**
     * 버튼의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.$getButton().toggleAttr('disabled', disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();

        if (this.minWidth !== null || this.width !== null) {
            this.$getComponent().addClass('fixed');
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        if (this.icon !== null) {
            this.setIcon(this.icon);
        }

        if (this.text !== null) {
            this.setText(this.text);
        }

        if (this.pressed === true) {
            this.setPressed(true);
        }

        this.$getContent().append(this.$getButton());

        if (this.handler !== null && this.menu !== null) {
            this.$getContent().append(this.$getDropdown());
        }

        this.$getContainer().append(this.$getContent());
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (this.menu !== null) {
            this.menu.remove();
        }

        super.remove(removeDom);
    }

    /**
     * 버튼 클릭이벤트를 처리한다.
     */
    onClick(): void {
        if (this.toggle == true) {
            this.setPressed(!this.pressed);
            return;
        }

        if (this.handler !== null) {
            this.handler(this, this.value);
        }
    }

    /**
     * 포커스 해제시 이벤트를 처리한다.
     *
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onBlur(): Promise<boolean> {
        if (this.menu?.isShow() == true) {
            this.menu.hide();
        }

        return await super.onBlur();
    }
}
